/**
 * Ritual Live Notification Component
 * Displays a notification when a ritual goes live
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getRitualTypeLabel } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { RitualNotificationCard } from "./RitualNotificationCard"

interface RitualLiveNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface RitualLiveNotificationData {
  ritualId?: string
  ritualName?: string
  ritualCoverImageUrl?: string | null
  isPaid?: boolean
  fee?: number | null
  duration?: string
  ritualType?: string
  durationType?: string
  accessType?: string
}

export function RitualLiveNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: RitualLiveNotificationProps) {
  const data = notification.data as RitualLiveNotificationData

  const ritualId = data.ritualId
  const ritualName = data.ritualName || "a ritual"
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isPaid = data.isPaid
  const fee = data.fee
  const duration = data.duration
  const durationType = data.durationType
  const ritualType = data.ritualType
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const ritualTypeLabel = ritualType ? getRitualTypeLabel(ritualType) : ""

  const formattedDuration = formatDuration(duration, durationType)

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/rituals/${ritualId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px] pr-20">
              {data.accessType === "PUBLIC"
                ? "Your ritual is now live and open for participants. People can discover it, join, and engage with your session in real time."
                : "Your ritual is now live, you can find it in your profile."}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <RitualNotificationCard
        coverImageUrl={ritualCoverImageUrl}
        title={ritualName}
        isPaid={isPaid}
        fee={fee}
        ritualTypeLabel={ritualTypeLabel}
        duration={formattedDuration}
        onClick={handleRitualClick}
      />
    </div>
  )
}
